import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RuleType } from '@client/graphql/__generated__/types';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { ComponentProps, FC } from 'react';
import { FieldImageBox } from '~/common/components/DocumentPDF/FieldImageBox';
import { ValidationItem } from '../utils/validation.utils';
import { ValidationItemDiffData } from '../utils/validationItem.utils';
import { ValidationTooltipContent } from './ValidationTooltipContent';

interface EnhancedValidationTooltipContentProps
  extends ComponentProps<typeof ValidationTooltipContent> {
  diffData?: ValidationItemDiffData[];
  fieldImages?: Record<string, string>;
  validationItem?: ValidationItem;
}

/**
 * [MARK] Enhanced validation tooltip content component, USE FOR INTERNAL ONLY!!
 */
export const EnhancedValidationTooltipContent: FC<
  EnhancedValidationTooltipContentProps
> = ({
  inputValues,
  selectedDocument,
  diffData,
  fieldImages,
  validationItem,
}) => {
  const hasDiffData = diffData && diffData.length > 0;
  const matchedFieldImages = inputValues?.map((iv) => {
    const image = fieldImages?.[iv.fieldKey];
    if (image) {
      return {
        fieldKey: iv.fieldKey,
        image,
      };
    }

    return null;
  });

  return (
    <VStack align="stretch" spacing={3}>
      <ValidationTooltipContent
        inputValues={inputValues}
        selectedDocument={selectedDocument}
      />

      {hasDiffData && (
        <Box>
          <Heading
            color="compliance.validation.tooltip.text.primary"
            mb={2}
            size="smallUppercase"
          >
            Validation Changes
          </Heading>
          <HStack flexWrap="wrap" spacing={2}>
            {diffData.map((diff) => {
              const themePrefix =
                diff.changeType === 'improved'
                  ? 'compliance.diff.improved'
                  : diff.changeType === 'degraded'
                    ? 'compliance.diff.degraded'
                    : 'compliance.diff.unchanged';

              return (
                <Badge
                  key={diff.fieldKey}
                  bg={`${themePrefix}.bg`}
                  borderColor={`${themePrefix}.border`}
                  color={`${themePrefix}.text`}
                  px={2}
                  size="sm"
                  textTransform="none"
                  variant="outline"
                >
                  <HStack spacing={1}>
                    {diff.reductionCount !== 0 && (
                      <Icon
                        as={diff.reductionCount > 0 ? TrendingUp : TrendingDown}
                        color={`${themePrefix}.icon`}
                        size={10}
                      />
                    )}
                    <Text fontSize="xs">{diff.fieldLabel}</Text>
                  </HStack>
                </Badge>
              );
            })}
          </HStack>
        </Box>
      )}

      {matchedFieldImages && matchedFieldImages.length > 0 && (
        <Box>
          <Heading
            color="compliance.validation.tooltip.text.primary"
            mb={2}
            size="smallUppercase"
          >
            Field Images
          </Heading>
          <Flex flexWrap="wrap" gap={2}>
            {matchedFieldImages.map((fieldImage) => {
              if (!fieldImage) {
                return null;
              }

              return (
                <FieldImageBox
                  key={fieldImage.fieldKey}
                  image={fieldImage.image}
                  inputValues={inputValues}
                  mappingKey={fieldImage.fieldKey}
                />
              );
            })}
          </Flex>
        </Box>
      )}

      {validationItem &&
        (validationItem.ruleFormula ||
          validationItem.ruleType ||
          validationItem.isDocumentValidation !== undefined ||
          validationItem.isPipelineValidation !== undefined) && (
          <Box>
            <Heading
              color="compliance.validation.tooltip.text.primary"
              mb={2}
              size="smallUppercase"
            >
              Technical Details
            </Heading>
            <Box bg="compliance.card.bg" borderRadius="md" p={3}>
              <VStack align="stretch" spacing={2}>
                {validationItem.ruleType && (
                  <HStack justify="space-between">
                    <Text
                      color="compliance.debug.text"
                      fontSize="xs"
                      fontWeight="medium"
                      opacity={0.7}
                    >
                      Rule Type:
                    </Text>
                    <Badge
                      bg={
                        validationItem.ruleType === RuleType.ERROR
                          ? 'red.50'
                          : validationItem.ruleType === RuleType.WARNING
                            ? 'orange.50'
                            : 'gray.50'
                      }
                      color={
                        validationItem.ruleType === RuleType.ERROR
                          ? 'red.600'
                          : validationItem.ruleType === RuleType.WARNING
                            ? 'orange.600'
                            : 'gray.600'
                      }
                      size="sm"
                      variant="subtle"
                    >
                      {validationItem.ruleType}
                    </Badge>
                  </HStack>
                )}

                {(validationItem.isDocumentValidation !== undefined ||
                  validationItem.isPipelineValidation !== undefined) && (
                  <HStack justify="space-between">
                    <Text
                      color="compliance.debug.text"
                      fontSize="xs"
                      fontWeight="medium"
                      opacity={0.7}
                    >
                      Validation Type:
                    </Text>
                    <Badge
                      bg={
                        validationItem.isDocumentValidation
                          ? 'blue.50'
                          : validationItem.isPipelineValidation
                            ? 'purple.50'
                            : 'gray.50'
                      }
                      color={
                        validationItem.isDocumentValidation
                          ? 'blue.600'
                          : validationItem.isPipelineValidation
                            ? 'purple.600'
                            : 'gray.600'
                      }
                      size="sm"
                      variant="subtle"
                    >
                      {validationItem.isDocumentValidation
                        ? 'Document'
                        : validationItem.isPipelineValidation
                          ? 'Pipeline'
                          : 'Unknown'}
                    </Badge>
                  </HStack>
                )}

                {validationItem.ruleFormula && (
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text
                        color="compliance.debug.text"
                        fontSize="xs"
                        fontWeight="medium"
                        opacity={0.7}
                      >
                        Rule Formula:
                      </Text>
                    </HStack>
                    <Box
                      bg="compliance.card.bg"
                      borderRadius="sm"
                      maxH="100px"
                      overflow="auto"
                      py={2}
                    >
                      <Text
                        color="compliance.debug.text"
                        fontFamily="mono"
                        fontSize="xs"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                      >
                        {validationItem.ruleFormula}
                      </Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            </Box>
          </Box>
        )}
    </VStack>
  );
};
